/**
 * Enregistrement du service worker + mise à jour contrôlée (docs/03 §4).
 * Quand une nouvelle version est installée et en attente, un toast propose
 * « Recharger » ; l'app ne recharge JAMAIS sans un geste explicite du joueur
 * (pas de reload forcé en pleine partie). La détection est séparée du DOM
 * pour être testable sans navigateur (pwa.test.ts).
 */

/** Sous-ensemble de ServiceWorkerRegistration utilisé (mockable en test). */
export interface RegistrationLike {
  waiting: { postMessage(data: unknown): void } | null;
  installing: { state: string; addEventListener(t: 'statechange', cb: () => void): void } | null;
  addEventListener(t: 'updatefound', cb: () => void): void;
}

/**
 * Signale via `onUpdateReady(apply)` qu'une nouvelle version attend. `apply()` demande
 * au SW en attente de prendre la main (SKIP_WAITING). `hasController` distingue une
 * mise à jour d'une toute première installation (pas de toast dans ce cas).
 */
export function watchUpdates(
  reg: RegistrationLike,
  hasController: () => boolean,
  onUpdateReady: (apply: () => void) => void,
): void {
  const offer = (): void => {
    const w = reg.waiting;
    if (w) onUpdateReady(() => w.postMessage({ type: 'SKIP_WAITING' }));
  };
  if (reg.waiting && hasController()) offer();
  reg.addEventListener('updatefound', () => {
    const inst = reg.installing;
    if (!inst) return;
    inst.addEventListener('statechange', () => {
      if (inst.state === 'installed' && hasController()) offer();
    });
  });
}

let updateAccepted = false;

function showUpdateToast(apply: () => void): void {
  const toast = document.getElementById('updToast');
  const btn = document.getElementById('updReload');
  if (!toast || !btn) return;
  toast.hidden = false;
  btn.addEventListener(
    'click',
    () => {
      updateAccepted = true;
      apply(); // → SKIP_WAITING → controllerchange → reload ci-dessous
    },
    { once: true },
  );
}

/** À appeler au boot. Inactif en dev (le SW ne sert que le build de production). */
export function initPwa(): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  void navigator.serviceWorker
    .register('/sw.js')
    .then((reg) => {
      watchUpdates(reg, () => navigator.serviceWorker.controller !== null, showUpdateToast);
    })
    .catch(() => {
      /* SW indisponible : l'app fonctionne normalement, en ligne */
    });
  // `controllerchange` arrive aussi au tout premier clients.claim() : on ne recharge
  // que si le joueur a explicitement accepté la mise à jour.
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (updateAccepted) {
      updateAccepted = false;
      location.reload();
    }
  });
}
